// RECALL-PEOPLE-SURFACES (docs/design/2026-10-31-recall-full-integration.md §5.1): what a member
// sees and does about their own memory. Three small things and nothing else: Profile › Memory
// (what memory knows about me, fix, forget, the holds that touch me, "Remember my DMs"), "Off the
// record" on a conversation, and "Remember this" on a message or a selection.
//
// The person doors (`about_me`, `correct_record`, the memory mode) are not live: until the memory
// service offers the `me` act, `waiting` carries "Waiting on memory service" and nothing errors.
// "Forget this" itself is `memoryForget.*` (memory-governance.ts); this file only lists the
// records it can name.
import { z } from "zod";
import { Timestamp, text, Uuid } from "../primitives";
import { CompanyParams } from "./common";
import { defineRoute } from "./route";

const co = "/v1/companies/{co}";
const memory = { feature: "memory", tag: "memory" } as const;

/** One line of what memory knows about the person, and the target "forget this" names for it. */
export const MemoryAboutLine = z
  .object({
    type: z.string(),
    title: z.string(),
    text: z.string(),
    /** `<personal space id>/<type>/<title>`: send it to `memoryForget.step` as a record. */
    target: z.string(),
  })
  .meta({ id: "MemoryAboutLine" });
export type MemoryAboutLine = z.infer<typeof MemoryAboutLine>;

/** A legal hold that covers this person's memory: only its case reference and reach. */
export const MemoryMeHold = z
  .object({
    id: Uuid,
    scope: z.enum(["person", "workspace"]),
    case_ref: z.string(),
    until: Timestamp.nullable(),
  })
  .meta({ id: "MemoryMeHold" });
export type MemoryMeHold = z.infer<typeof MemoryMeHold>;

export const MemoryMeView = z
  .object({
    capabilities: z.object({ me: z.boolean(), waiting_text: z.string() }),
    about: z.object({
      /** The memory service does not offer the person doors yet: the lines are not known. */
      waiting: z.string().nullable(),
      lines: z.array(MemoryAboutLine),
    }),
    holds: z.array(MemoryMeHold),
    remember_dms: z.object({
      on: z.boolean(),
      /** The memory service has been told the switch; false: it stands here until it is offered. */
      mirrored: z.boolean(),
    }),
  })
  .meta({ id: "MemoryMeView" });
export type MemoryMeView = z.infer<typeof MemoryMeView>;

export const MemoryMeAct = z
  .discriminatedUnion("action", [
    z.strictObject({
      action: z.literal("fix"),
      type: text(80),
      title: text(300),
      text: text(2000),
    }),
    z.strictObject({ action: z.literal("dms"), on: z.boolean() }),
  ])
  .meta({ id: "MemoryMeAct" });
export type MemoryMeAct = z.infer<typeof MemoryMeAct>;

export const MemoryMeResult = z
  .object({
    /** Set when the memory service does not offer the door yet; nothing was lost. */
    waiting: z.string().nullable(),
    view: MemoryMeView,
  })
  .meta({ id: "MemoryMeResult" });
export type MemoryMeResult = z.infer<typeof MemoryMeResult>;

/** "Remember this": the person's own words (or a selection), kept in their own personal memory. */
export const MemoryRemember = z
  .strictObject({
    text: text(2000),
    /** A short name for the note; default: its first words. */
    title: text(120).optional(),
    from: z.enum(["message", "doc", "chat"]).optional(),
  })
  .meta({ id: "MemoryRemember" });
export type MemoryRemember = z.infer<typeof MemoryRemember>;

export const MemoryRememberResult = z
  .object({
    remembered: z.boolean(),
    title: z.string(),
    waiting: z.string().nullable(),
  })
  .meta({ id: "MemoryRememberResult" });
export type MemoryRememberResult = z.infer<typeof MemoryRememberResult>;

export const OffRecordItem = z
  .object({ channel_id: Uuid, since: Timestamp })
  .meta({ id: "OffRecordItem" });

/** The conversations the caller has put off the record right now. */
export const OffRecordList = z
  .object({ items: z.array(OffRecordItem) })
  .meta({ id: "OffRecordList" });
export type OffRecordList = z.infer<typeof OffRecordList>;

export const OffRecordSet = z
  .strictObject({ channel_id: Uuid, on: z.boolean() })
  .meta({ id: "OffRecordSet" });
export type OffRecordSet = z.infer<typeof OffRecordSet>;

const EVENT = ["memory.governance_changed"] as const;

export const memoryPeopleRoutes = {
  "memoryMe.get": defineRoute({
    ...memory,
    id: "memoryMe.get",
    method: "get",
    path: `${co}/memory/me`,
    summary: "Profile › Memory: what memory knows about you, your holds, and Remember my DMs",
    access: "member",
    params: CompanyParams,
    response: { status: 200, schema: MemoryMeView },
    errors: [],
    emits: [],
  }),
  "memoryMe.act": defineRoute({
    ...memory,
    id: "memoryMe.act",
    method: "post",
    path: `${co}/memory/me`,
    summary: "Fix a line memory knows about you, or turn Remember my DMs on or off",
    description:
      "Acts as you. A fix is a memory-service door that is not offered yet: the answer is 200 with `waiting`. The DM switch is kept here and told to the memory service once it offers the door.",
    access: "member",
    params: CompanyParams,
    body: MemoryMeAct,
    response: { status: 200, schema: MemoryMeResult },
    errors: ["not_found", "forbidden"],
    emits: EVENT,
  }),
  "memoryRemember.post": defineRoute({
    ...memory,
    id: "memoryRemember.post",
    method: "post",
    path: `${co}/memory/remember`,
    summary: "Remember this: keep a message, a selection or a few words in your own memory",
    access: "member",
    params: CompanyParams,
    body: MemoryRemember,
    response: { status: 200, schema: MemoryRememberResult },
    errors: ["forbidden"],
    emits: EVENT,
  }),
  "memoryOffRecord.get": defineRoute({
    ...memory,
    id: "memoryOffRecord.get",
    method: "get",
    path: `${co}/memory/off-record`,
    summary: "The conversations you have put off the record",
    access: "member",
    params: CompanyParams,
    response: { status: 200, schema: OffRecordList },
    errors: [],
    emits: [],
  }),
  "memoryOffRecord.set": defineRoute({
    ...memory,
    id: "memoryOffRecord.set",
    method: "post",
    path: `${co}/memory/off-record`,
    summary: "Off the record: from now until you turn it off, nothing said in this chat is kept",
    access: "member",
    params: CompanyParams,
    body: OffRecordSet,
    response: { status: 200, schema: OffRecordList },
    errors: ["not_found", "forbidden"],
    emits: EVENT,
  }),
} as const;
